import { useMemo } from 'react';
import jwtDecode from 'jwt-decode';
import { AuthService } from 'services/authservice';

/**
 * Hook que devuelve el "scope" tenant del usuario logueado leyendolo
 * directamente del JWT.
 *
 * Por que el JWT y no `useAuth().userData`?
 *  - `userData` se llena con `Axios.get('users/${id}')` (ver
 *    `providers/authProviders.jsx`), pero `application-server-v2` no
 *    expone `GET /users/:id`. Cuando ese call falla silenciosamente,
 *    `userData` queda `null` y todo filtro tenant deja de aplicarse.
 *  - El JWT ya viaja con `role.type`, `companyId`, `logisticCompanyId`
 *    y `holdingId/holding.companies` populated (ver
 *    `bff-auth-gateway/src/api/auth/auth.service.ts`).
 *
 * Uso:
 *  const { roleType, companyId, logisticCompanyId, holdingId, holdingCompanies } = useAuthScope();
 *
 * @returns {{
 *   roleType: string|null,
 *   companyId: string|null,
 *   logisticCompanyId: string|null,
 *   holdingId: string|null,
 *   holdingCompanies: string[],
 * }}
 */
const extractId = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'object') return value._id || value.id || null;
  return null;
};

export default function useAuthScope() {
  return useMemo(() => {
    const empty = {
      roleType: null,
      companyId: null,
      logisticCompanyId: null,
      holdingId: null,
      holdingCompanies: [],
      holding: null,
    };
    const token = AuthService.getAccessTokenApi();
    if (!token) return empty;
    try {
      const decoded = jwtDecode(token);
      const holdingClaim = decoded?.holding ?? null;
      const holdingCompaniesRaw = holdingClaim?.companies;
      return {
        roleType: decoded?.role?.type ?? null,
        companyId: extractId(decoded?.companyId),
        logisticCompanyId: extractId(decoded?.logisticCompanyId),
        holdingId: extractId(decoded?.holdingId) || extractId(holdingClaim?._id),
        holdingCompanies: Array.isArray(holdingCompaniesRaw)
          ? holdingCompaniesRaw.map((c) => extractId(c)).filter(Boolean)
          : [],
        holding: holdingClaim,
      };
    } catch (e) {
      return empty;
    }
  }, []);
}
